import { applyAppUpdate, prefetchAppUpdate } from '../pwa';

export interface AppVersionInfo {
  buildId: string;
  builtAt: string;
}

const VERSION_URL = '/version.json';

async function fetchRemoteBuildId(): Promise<string | null> {
  try {
    const response = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' },
    });
    if (!response.ok) return null;

    const data = (await response.json()) as AppVersionInfo;
    return data.buildId ?? null;
  } catch {
    return null;
  }
}

/** Polls /version.json on focus — reliable update path on iOS standalone PWA. */
export function startAppVersionWatch(onUpdateAvailable: () => void): () => void {
  if (import.meta.env.DEV) {
    return () => {};
  }

  const localBuildId = __APP_BUILD_ID__;
  let notified = false;

  const check = async () => {
    if (notified || document.visibilityState === 'hidden') return;

    const remoteBuildId = await fetchRemoteBuildId();
    if (remoteBuildId && remoteBuildId !== localBuildId) {
      notified = true;
      prefetchAppUpdate();
      onUpdateAvailable();
    }
  };

  void check();

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      void check();
    }
  };

  const onFocus = () => {
    void check();
  };

  const onPageShow = (event: PageTransitionEvent) => {
    if (event.persisted) {
      void check();
    }
  };

  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('focus', onFocus);
  window.addEventListener('pageshow', onPageShow);

  const intervalId = window.setInterval(() => {
    void check();
  }, 60 * 60 * 1000);

  return () => {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('focus', onFocus);
    window.removeEventListener('pageshow', onPageShow);
    window.clearInterval(intervalId);
  };
}

export function reloadForUpdate(): void {
  void applyAppUpdate();
}
